import React, { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { useModule, usePublishModule, useLessonsByModule, useCreateLesson, useUpdateLesson, useDeleteLesson, useSetLessonPreviewable, useCourse } from '@/hooks'
import type { Lesson, CreateLessonDTO, UpdateLessonDTO, LessonContentType, LessonContentFormat } from '@/types/lesson'
import { LessonFormModal } from '@/components/lessons/LessonFormModal'
import { QuizManager } from '@/components/quiz'
import { AssignmentManager } from '@/components/assignments'
import { ModuleStatusBadge, PublishModuleModal } from '@/components/modules'
import { ModuleInstructorCard } from '@/components/modules/ModuleInstructorCard'

interface LessonFormData {
  title: string
  description: string
  contentType: LessonContentType
  contentUrl: string
  contentHtml: string
  contentFormat: LessonContentFormat
  durationMinutes: number
  isPreviewable: boolean
  isMandatory: boolean
}

export const AdminModuleEditorPage: React.FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>()
  const navigate = useNavigate()

  const [showLessonModal, setShowLessonModal] = useState(false)
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null)
  const [deletingLessonId, setDeletingLessonId] = useState<string | null>(null)
  const [expandedLessonId, setExpandedLessonId] = useState<string | null>(null)
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Fetch module and lessons
  const { data: module, isLoading: moduleLoading, error: moduleError } = useModule(moduleId || '')
  const { data: lessons, isLoading: lessonsLoading, refetch: refetchLessons } = useLessonsByModule(moduleId || '')

  // Fetch course for breadcrumb
  const { data: course, isLoading: courseLoading } = useCourse(module?.courseId || '')

  // Mutations
  const createLesson = useCreateLesson()
  const updateLesson = useUpdateLesson()
  const deleteLesson = useDeleteLesson()
  const setPreviewable = useSetLessonPreviewable()
  const publishModule = usePublishModule()

  const isLoading = moduleLoading || lessonsLoading || courseLoading

  const handleAddLesson = () => {
    setEditingLesson(null)
    setShowLessonModal(true)
  }

  const handleEditLesson = (lesson: Lesson) => {
    setEditingLesson(lesson)
    setShowLessonModal(true)
  }

  const handleDeleteLesson = async (lessonId: string) => {
    if (!confirm('Are you sure you want to delete this lesson?')) return

    setDeletingLessonId(lessonId)
    try {
      await deleteLesson.mutateAsync(lessonId)
      refetchLessons()
    } catch (error) {
      console.error('Failed to delete lesson:', error)
    } finally {
      setDeletingLessonId(null)
    }
  }

  const handleSaveLesson = async (data: LessonFormData) => {
    setErrorMessage(null)
    try {
      if (editingLesson) {
        const updateData: UpdateLessonDTO = {
          title: data.title,
          description: data.description,
          contentType: data.contentType,
          contentUrl: data.contentUrl,
          contentHtml: data.contentHtml,
          contentFormat: data.contentFormat,
          durationMinutes: data.durationMinutes,
          isPreviewable: data.isPreviewable,
          isMandatory: data.isMandatory,
        }
        const response = await updateLesson.mutateAsync({
          id: editingLesson.id,
          data: updateData,
        })
        if (!response.success) {
          const details = typeof response.error?.details === 'string'
            ? response.error.details
            : response.error?.details?.map(d => `${d.field}: ${d.message}`).join(', ')
          throw new Error(details || response.error?.message || 'Failed to update lesson')
        }
      } else {
        const createData: CreateLessonDTO = {
          moduleId: moduleId || '',
          title: data.title,
          description: data.description,
          contentType: data.contentType,
          contentUrl: data.contentUrl,
          contentHtml: data.contentHtml,
          contentFormat: data.contentFormat,
          durationMinutes: data.durationMinutes,
          orderIndex: lessons?.length || 0,
          isPreviewable: data.isPreviewable,
          isMandatory: data.isMandatory,
        }
        const response = await createLesson.mutateAsync(createData)
        if (!response.success) {
          const details = typeof response.error?.details === 'string'
            ? response.error.details
            : response.error?.details?.map(d => `${d.field}: ${d.message}`).join(', ')
          throw new Error(details || response.error?.message || 'Failed to create lesson')
        }
      }
      refetchLessons()
      setShowLessonModal(false)
    } catch (error: unknown) {
      let errMsg = 'An unexpected error occurred'
      if (error instanceof Error) {
        errMsg = error.message
      } else if (typeof error === 'object' && error !== null) {
        const err = error as { message?: string; error?: { message?: string; details?: string } }
        errMsg = err.error?.details || err.error?.message || err.message || errMsg
      }
      console.error('Failed to save lesson:', errMsg, error)
      setErrorMessage(errMsg)
    }
  }

  const handleTogglePreview = async (lesson: Lesson) => {
    try {
      await setPreviewable.mutateAsync({
        id: lesson.id,
        previewable: !lesson.isPreviewable,
      })
      refetchLessons()
    } catch (error) {
      console.error('Failed to toggle preview:', error)
    }
  }

  const handlePublishModule = async () => {
    if (!module) return
    try {
      await publishModule.mutateAsync(module.id)
      setShowPublishModal(false)
    } catch (error) {
      console.error('Failed to publish module:', error)
    }
  }

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'video':
        return 'play_circle'
      case 'pdf':
        return 'picture_as_pdf'
      case 'text':
        return 'article'
      default:
        return 'description'
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-500 dark:text-text-muted">Loading module...</p>
        </div>
      </div>
    )
  }

  if (moduleError) {
    return (
      <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-6 text-center">
        <span className="material-symbols-outlined text-red-400 text-4xl mb-3">error</span>
        <p className="text-red-400 font-medium text-lg">Failed to load module</p>
        <p className="text-red-400/70 text-sm mt-2">{moduleError.message}</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white text-sm hover:bg-gray-200 dark:hover:bg-white/10"
        >
          Go Back
        </button>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title={module?.title || 'Module Editor'}
        subtitle="Manage lessons and activities"
        breadcrumbs={[
          { label: 'Dashboard', to: '/admin/dashboard' },
          { label: 'Courses', to: '/admin/courses' },
          { label: course?.title || 'Course', to: `/admin/courses/${module?.courseId}/builder` },
          { label: module?.title || 'Module' }
        ]}
        actions={
          <Link
            to={`/admin/courses/${module?.courseId}/builder`}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white rounded-lg font-bold text-sm hover:bg-white/20 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            Back to Course
          </Link>
        }
      />

      {/* Module Info Card */}
      <div className="group relative rounded-[24px] bg-white dark:bg-surface-card border border-slate-200 dark:border-white/5 p-8 mb-8 overflow-hidden shadow-sm hover:shadow-xl hover:shadow-primary/5 transition-all duration-500">
        <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform duration-700">
          <span className="material-symbols-outlined text-[120px] text-primary">view_module</span>
        </div>
        
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <ModuleStatusBadge isDraft={module?.isDraft || false} />
            </div>
            <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">{module?.title}</h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm font-medium max-w-2xl leading-relaxed">{module?.description || 'No description available for this module.'}</p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="flex items-center gap-6 text-sm font-black tracking-wider text-slate-400">
              <div className="flex items-center gap-2">
                <div className="size-9 rounded-xl bg-slate-100 dark:bg-white/5 flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined text-[20px]">schedule</span>
                </div>
                <span>{module?.durationHours || 0} Jam</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="size-9 rounded-xl bg-slate-100 dark:bg-white/5 flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined text-[20px]">play_circle</span>
                </div>
                <span>{lessons?.length || 0} Materi</span>
              </div>
            </div>
            
            {module?.isDraft && (
              <button
                onClick={() => setShowPublishModal(true)}
                className="flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-emerald-500 text-white text-sm font-black tracking-tight hover:scale-[1.05] active:scale-[0.95] transition-all shadow-lg shadow-emerald-500/20"
              >
                <span className="material-symbols-outlined text-[18px]">publish</span>
                Publish Module
              </button>
            )}
          </div>
        </div>

        {/* Module Instructor Assignment */}
        {module?.courseId && moduleId && (
          <div className="mt-8 pt-8 border-t border-slate-100 dark:border-white/5">
            <ModuleInstructorCard moduleId={moduleId} courseId={module.courseId} />
          </div>
        )}
      </div>

      {/* Lessons Section Header */}
      <div className="flex items-center justify-between mb-6 px-1">
        <div className="flex items-center gap-3">
          <div className="size-2 w-2 h-8 bg-primary rounded-full" />
          <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">Daftar Materi</h3>
        </div>
        <button
          onClick={handleAddLesson}
          className="flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-black tracking-wider hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-slate-200 dark:shadow-none"
        >
          <span className="material-symbols-outlined text-[18px]">add_circle</span>
          Tambah Materi
        </button>
      </div>

      {/* Lessons List */}
      {!lessons || lessons.length === 0 ? (
        <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 border-dashed p-12 text-center">
          <div className="size-16 rounded-full bg-gray-100 dark:bg-white/5 mx-auto mb-4 flex items-center justify-center">
            <span className="material-symbols-outlined text-[32px] text-gray-500">add_circle</span>
          </div>
          <p className="text-gray-900 dark:text-white font-bold mb-2">No lessons yet</p>
          <p className="text-gray-500 dark:text-text-muted text-sm mb-4">Add your first lesson to this module</p>
          <button
            onClick={handleAddLesson}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary-hover transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">add</span>
            Add First Lesson
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {lessons.map((lesson: Lesson, index: number) => {
            const isExpanded = expandedLessonId === lesson.id
            return (
              <div
                key={lesson.id}
                className={`group rounded-[24px] bg-white dark:bg-surface-card border transition-all duration-500 ${
                  isExpanded 
                    ? 'border-primary/50 shadow-2xl shadow-primary/5 scale-[1.01]' 
                    : 'border-slate-200 dark:border-white/5 hover:border-primary/30 hover:shadow-xl hover:shadow-slate-200/50 dark:hover:shadow-none'
                }`}
              >
                <div className="p-6">
                  <div className="flex items-center gap-6">
                    {/* Order & Type */}
                    <div className="relative shrink-0">
                      <div className="size-14 rounded-2xl bg-slate-50 dark:bg-white/[0.03] flex items-center justify-center text-slate-400 group-hover:text-primary transition-colors duration-500">
                        <span className="material-symbols-outlined text-[28px]">
                          {getContentTypeIcon(lesson.contentType)}
                        </span>
                      </div>
                      <div className="absolute -top-2 -left-2 size-6 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center text-[10px] font-black shadow-lg">
                        {index + 1}
                      </div>
                    </div>
                    
                    {/* Lesson Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-1.5">
                        <h4 className="text-base font-black text-slate-900 dark:text-white truncate tracking-tight">{lesson.title}</h4>
                        <div className="flex gap-2">
                          {lesson.isPreviewable && (
                            <span className="px-2.5 py-1 rounded-lg text-xs font-black tracking-widest bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                              Free Preview
                            </span>
                          )}
                          {lesson.isMandatory && (
                            <span className="px-2.5 py-1 rounded-lg text-xs font-black tracking-widest bg-amber-500/10 text-amber-500 border border-amber-500/20">
                              Required
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs font-bold text-slate-400 tracking-wider">
                        <span className="flex items-center gap-1.5">
                          <span className="material-symbols-outlined text-[16px]">category</span>
                          {lesson.contentType}
                        </span>
                        <span className="size-1 rounded-full bg-slate-300 dark:bg-white/10" />
                        <span className="flex items-center gap-1.5">
                          <span className="material-symbols-outlined text-[16px]">schedule</span>
                          {lesson.durationMinutes} Menit
                        </span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-4 shrink-0">
                      {/* Free Preview Toggle */}
                      <div className="hidden lg:flex items-center gap-3 pr-4 border-r border-slate-100 dark:border-white/5">
                        <span className={`text-xs font-black tracking-wider ${lesson.isPreviewable ? 'text-emerald-500' : 'text-slate-400'}`}>
                          Preview
                        </span>
                        <button
                          onClick={() => handleTogglePreview(lesson)}
                          disabled={setPreviewable.isPending}
                          className={`relative w-10 h-5 rounded-full transition-all duration-300 ${
                            lesson.isPreviewable ? 'bg-emerald-500 shadow-lg shadow-emerald-500/20' : 'bg-slate-200 dark:bg-white/10'
                          } ${setPreviewable.isPending ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                        >
                          <span className={`absolute top-1 left-1 size-3 rounded-full bg-white transition-transform duration-300 ${lesson.isPreviewable ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Activities Button */}
                        <button
                          onClick={() => setExpandedLessonId(isExpanded ? null : lesson.id)}
                          className={`size-11 rounded-xl transition-all duration-300 flex items-center justify-center ${
                            isExpanded 
                              ? 'bg-primary text-white shadow-lg shadow-primary/30' 
                              : 'bg-slate-100 dark:bg-white/5 text-slate-500 hover:text-primary hover:bg-primary/10'
                          }`}
                          title="Quiz & Assignment"
                        >
                          <span className="material-symbols-outlined text-[20px]">quiz</span>
                        </button>
                        
                        {/* Edit Button */}
                        <button
                          onClick={() => handleEditLesson(lesson)}
                          className="size-11 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-white/10 transition-all duration-300 flex items-center justify-center"
                          title="Edit"
                        >
                          <span className="material-symbols-outlined text-[20px]">edit_note</span>
                        </button>
                        
                        {/* Delete Button */}
                        <button
                          onClick={() => handleDeleteLesson(lesson.id)}
                          disabled={deletingLessonId === lesson.id}
                          className="size-11 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-500 hover:text-rose-500 hover:bg-rose-500/10 transition-all duration-300 flex items-center justify-center disabled:opacity-50"
                          title="Hapus"
                        >
                          <span className="material-symbols-outlined text-[20px]">
                            {deletingLessonId === lesson.id ? 'hourglass_empty' : 'delete_sweep'}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Expanded Activities Section */}
                {isExpanded && (
                  <div className="border-t border-slate-100 dark:border-white/5 p-8 bg-slate-50/30 dark:bg-white/[0.01] animate-in slide-in-from-top-4 duration-500 rounded-b-[24px]">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="size-1.5 h-6 bg-primary rounded-full" />
                      <h4 className="text-sm font-black text-slate-900 dark:text-white tracking-wider">Aktivitas Belajar: {lesson.title}</h4>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Quiz Manager */}
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 text-xs font-black text-slate-400 tracking-wider mb-2">
                          <span className="material-symbols-outlined text-sm">quiz</span>
                          Manajemen Quiz
                        </div>
                        <div className="rounded-2xl border border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] p-2">
                          <QuizManager lessonId={lesson.id} lessonTitle={lesson.title} />
                        </div>
                      </div>
                      
                      {/* Assignment Manager */}
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 text-xs font-black text-slate-400 tracking-wider mb-2">
                          <span className="material-symbols-outlined text-sm">assignment</span>
                          Manajemen Tugas
                        </div>
                        <div className="rounded-2xl border border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] p-2">
                          <AssignmentManager lessonId={lesson.id} lessonTitle={lesson.title} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Lesson Form Modal */}
      {showLessonModal && (
        <LessonFormModal
          lesson={editingLesson}
          onSave={handleSaveLesson}
          onClose={() => setShowLessonModal(false)}
          isSaving={createLesson.isPending || updateLesson.isPending}
        />
      )}

      {/* Publish Module Modal */}
      {showPublishModal && module && (
        <PublishModuleModal
          module={module}
          lessons={lessons || []}
          onPublish={handlePublishModule}
          onClose={() => setShowPublishModal(false)}
          isPublishing={publishModule.isPending}
        />
      )}

      {/* Error Modal */}
      {errorMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-surface-card rounded-lg p-6 max-w-md w-full mx-4 border border-red-500/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="size-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <span className="material-symbols-outlined text-red-400">error</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Error</h3>
            </div>
            <p className="text-gray-500 dark:text-text-muted mb-6 whitespace-pre-wrap">{errorMessage}</p>
            <button
              onClick={() => setErrorMessage(null)}
              className="w-full py-2.5 rounded-lg bg-red-500 text-white font-bold hover:bg-red-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminModuleEditorPage
